import Vue from 'vue'
import VueRouter from 'vue-router'
import Home from '../views/home.vue'
import { BRAND_NAME, canonical } from '@/config/myvpnbookSite'
import { locationPages } from '@/data/myvpnbookLocations'
import { myvpnbookPlatformPages } from '@/data/myvpnbookPlatformPages'
import guides from '@/documents/myvpnbookGuides'

Vue.use(VueRouter)

const originalPush = VueRouter.prototype.push
VueRouter.prototype.push = function push(location) {
	return originalPush.call(this, location).catch((err) => err)
}

const metaPage = (title, description, keywords, path) => ({
	appname: BRAND_NAME,
	title,
	description,
	keywords,
	link: [{ rel: 'canonical', href: canonical(path) }],
})

const platformKeys = ['android', 'windows', 'mac', 'iphone', 'chrome']
const platformRoutes = platformKeys.map((key) => {
	const page = myvpnbookPlatformPages[key]
	return {
		path: page.path,
		name: page.name,
		props: { pageKey: key },
		meta: metaPage(page.title, page.description, page.keywords, page.path),
		component: () => import('@/views/PlatformPage.vue'),
	}
})

const locationRoutes = Object.entries(locationPages).map(([locationKey, page]) => ({
	path: page.path,
	name: `location-${locationKey}`,
	meta: {
		...metaPage(page.title, page.description, `MyVPNBook, ${page.h1}`, page.path),
		locationKey,
	},
	component: () => import('@/views/location-page.vue'),
}))

const guideRoutes = guides.map((guide) => ({
	path: guide.path,
	name: `guide-${guide.slug}`,
	meta: {
		...metaPage(guide.title, guide.description, 'MyVPNBook, VPN guide', guide.path),
		guideSlug: guide.slug,
	},
	component: () => import('@/views/guideArticle.vue'),
}))

export const constRoutesPc = [
	{
		path: '/',
		name: 'home',
		meta: metaPage(
			'MyVPNBook - Free VPN for Private and Secure Browsing',
			'MyVPNBook offers free VPN access, global VPN servers, and helpful setup guides for Android, Windows, iPhone, Mac, and more. Browse privately and securely online.',
			'free VPN, MyVPNBook, VPN servers, secure VPN',
			'/'
		),
		component: Home,
	},
	{
		path: '/get-free-vpn/',
		name: 'get-free-vpn',
		meta: metaPage(
			'Download Secure VPN APK Free – Latest Android Version',
			'Download Secure VPN APK free for Android. Get the latest version, install it manually, and enjoy a simple VPN connection with easy setup and no root required.',
			'Secure VPN APK, free VPN Android, MyVPNBook',
			'/get-free-vpn/'
		),
		component: () => import('@/views/get-free-vpn.vue'),
	},
	{ path: '/get-free-vpn', redirect: '/get-free-vpn/' },
	{ path: '/download/', redirect: '/get-free-vpn/' },
	{ path: '/download', redirect: '/get-free-vpn/' },
	...platformRoutes,
	...locationRoutes,
	{
		path: '/common-vpn-problems/',
		name: 'common-vpn-problems',
		meta: metaPage(
			'Common VPN Problems and How to Fix Them',
			'VPN not connecting, running slowly, or disconnecting by itself? Check the most common VPN problems and try simple fixes for connection, Wi-Fi, server, and setup issues.',
			'VPN not working, VPN troubleshooting, MyVPNBook',
			'/common-vpn-problems/'
		),
		component: () => import('@/views/common-vpn-problems.vue'),
	},
	{
		path: '/guides/',
		name: 'guides',
		meta: metaPage(
			'VPN Guides – VPN Tips, Setup & Troubleshooting',
			'Practical VPN guides covering privacy, IP addresses, public Wi-Fi, VPN setup, compatibility, passwords, and common questions.',
			'VPN guides, MyVPNBook',
			'/guides/'
		),
		component: () => import('@/views/guides.vue'),
	},
	...guideRoutes,
	{
		path: '/about-us/',
		name: 'about-us',
		meta: metaPage(
			'About MyVPNBook - VPN Guides, Servers & Resources',
			'Learn more about MyVPNBook, a VPN information website providing VPN guides, server information, setup help, troubleshooting tips, and privacy resources.',
			'About MyVPNBook',
			'/about-us/'
		),
		component: () => import('@/views/about-us.vue'),
	},
	{
		path: '/contact/',
		name: 'contact',
		meta: metaPage(
			'Contact MyVPNBook - Get in Touch',
			'Contact MyVPNBook with questions, corrections, feedback, copyright concerns, or issues related to our VPN guides and website content.',
			'Contact MyVPNBook',
			'/contact/'
		),
		component: () => import('@/views/contact.vue'),
	},
	{
		path: '/privacy-policy/',
		name: 'privacy-policy',
		meta: metaPage(
			'Privacy Policy - MyVPNBook',
			'Read the MyVPNBook Privacy Policy to understand how we collect, use, store, and protect information.',
			'MyVPNBook privacy policy',
			'/privacy-policy/'
		),
		component: () => import('@/views/privacy.vue'),
	},
	{
		path: '/terms/',
		name: 'terms',
		meta: metaPage(
			'Terms of Service - MyVPNBook',
			'Read the MyVPNBook Terms of Service covering website use, VPN information, third-party services, external links, content, limitations of liability, and user responsibilities.',
			'MyVPNBook terms',
			'/terms/'
		),
		component: () => import('@/views/terms.vue'),
	},
	{
		path: '/cookie-policy/',
		name: 'cookie-policy',
		meta: metaPage(
			'Cookie Policy - MyVPNBook',
			'Learn how MyVPNBook uses cookies and similar technologies for website functionality, analytics, advertising, and user preferences.',
			'MyVPNBook cookies',
			'/cookie-policy/'
		),
		component: () => import('@/views/cookie-policy.vue'),
	},
	{
		path: '/disclaimer/',
		name: 'disclaimer',
		meta: metaPage(
			'Disclaimer - MyVPNBook',
			'Read the MyVPNBook Disclaimer covering VPN information, third-party services, advertising, external links, accuracy, and user responsibility.',
			'MyVPNBook disclaimer',
			'/disclaimer/'
		),
		component: () => import('@/views/disclaimer.vue'),
	},
	{
		path: '/dmca/',
		name: 'dmca',
		meta: metaPage(
			'DMCA Policy - MyVPNBook',
			'Read the MyVPNBook DMCA Policy to report copyright infringement and request the review or removal of allegedly infringing content.',
			'MyVPNBook DMCA',
			'/dmca/'
		),
		component: () => import('@/views/dmca.vue'),
	},
]

export default function createRouter() {
	const router = new VueRouter({
		mode: 'history',
		routes: constRoutesPc,
		scrollBehavior(to, from, savedPosition) {
			if (savedPosition) return savedPosition
			if (to.hash) {
				return { selector: to.hash, behavior: 'smooth' }
			}
			return { x: 0, y: 0 }
		},
	})

	router.beforeEach((to, from, next) => {
		const isStaticFile = /\.[a-z0-9]+$/i.test(to.path)
		if (to.path !== '/' && !to.path.endsWith('/') && !isStaticFile) {
			next({ path: `${to.path}/`, query: to.query, hash: to.hash, replace: true })
			return
		}
		next()
	})

	return router
}
